// Unpublished Work © 2024 Deere & Company.

import { AccessToken } from "@okta/okta-auth-js";
import { useOktaAuth } from "@okta/okta-react";
import {
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import DealerApi, { Account } from "../../api/dealer/DealerApi";
import useDealerApi from "../../api/dealer/useDealerApi";
import RbacApi from "../../api/rbac/rbacApi";
import {
  BusinessAction,
  RBACRole,
  RBACRolesResponse,
} from "../../api/rbac/types";
import LoadingIndicator from "../../components/LoadingIndicator/LoadingIndicator";
import { ApplicationError, handleUserProviderErrors } from "../../router/utils";
import { decodeHtmlEntities } from "../../utils/formatters";
import { ConfigContext } from "../ConfigContext";
import {
  useDelegateContext,
  useEffectiveUserId,
} from "../DelegateContext/DelegateContext";
import { RBACAccessContext } from "../RBACAccessContext/RBACAccessContext";
import { isFeatureToggleRBACEnabled } from "../RBACAccessContext/RBACAcessUtil";

export interface UserDetails {
  name: string;
  xId: string;
  roles: RBACRole[];
  businessActivities: BusinessAction[];
  isAuthorizedTo: (businessAction: BusinessAction) => boolean;
  is: (role: RBACRole) => boolean;
  isUserWithJDRole: () => boolean;
  hasSetDefaultAccess: boolean;
  accounts: Account[];
}

const checkIfUserAuthorizedForAction = (
  action: BusinessAction,
  allowedActions: BusinessAction[]
): boolean => allowedActions.includes(action);

const checkIfUserAuthorizedForRole = (
  role: RBACRole,
  allowedRoles: RBACRole[]
): boolean => allowedRoles.includes(role);

const BUSINESS_ACTIONS_FOR_VIEW_ACCESS = [
  BusinessAction.VIEW_PROMOTIONS,
  BusinessAction.VIEW_PART_INFORMATION,
  BusinessAction.VIEW_CLAIMS,
  BusinessAction.VIEW_MY_DASHBOARD,
  BusinessAction.VIEW_ACCOUNT_PREFERENCE,
  BusinessAction.VIEW_INVOICES,
  BusinessAction.VIEW_RETURNS,
];

const JD_ROLES = [
  RBACRole.JD_ADMIN,
  RBACRole.JD_SUPER_ADMIN,
  RBACRole.JD_DEVELOPER_ADMIN,
  RBACRole.JD_READ_ONLY,
];

export const checkIfUserHasJDRole = (roles: RBACRole[]): boolean =>
  roles.some((role) => JD_ROLES.includes(role));

export const UserContext = createContext<UserDetails | undefined>(undefined);
export const useUserContext = () => useContext(UserContext);

export const loadAccountsAndRoles = async (
  rbacUrl: string,
  dealerUrl: string,
  token: AccessToken,
  userId: string,
  userDetails: Promise<RBACRolesResponse>,
  accountMappings: Promise<{ data: Account[] }>,
  ftRBACToggle: boolean,
  isDealerServiceApiAuthEnable: boolean = false,
  dealerApiHook: ReturnType<typeof useDealerApi>,
  enableCorporateViewError: boolean = false,
  isClientUI: boolean = true
): Promise<UserDetails> => {
  let hasDefaultAccess = false;
  const responses: RBACRolesResponse = await (isFeatureToggleRBACEnabled(
    ftRBACToggle
  )
    ? userDetails
    : RbacApi.getUserRoles(rbacUrl, token.accessToken, userId)
  )
    .then((response) => response)
    .catch((e: any) => {
      if (e.response && e.response.status === 401) throw e;
      hasDefaultAccess = true;
      return {
        userId,
        roles: [RBACRole.VIEW_ACCESS],
        businessActivities: BUSINESS_ACTIONS_FOR_VIEW_ACCESS,
      };
    });

  const firstName = decodeHtmlEntities(token.claims?.firstName as string);
  const lastName = decodeHtmlEntities(token.claims?.lastName as string);

  if (checkIfUserHasJDRole(responses.roles))
    return {
      xId: userId,
      roles: responses.roles,
      businessActivities: responses.businessActivities,
      name: `${firstName} ${lastName}`,
      isAuthorizedTo: (businessAction: BusinessAction) =>
        checkIfUserAuthorizedForAction(businessAction, []),
      isUserWithJDRole: () => checkIfUserHasJDRole(responses.roles),
      is: (role: RBACRole) =>
        checkIfUserAuthorizedForRole(role, responses.roles),
      hasSetDefaultAccess: hasDefaultAccess,
      accounts: [],
    };

  if (responses.roles.length === 0) {
    throw responses;
  }

  if (
    enableCorporateViewError &&
    responses.roles.length === 1 &&
    responses.roles.includes(RBACRole.CORPORATE_VIEW)
  ) {
    const corporateViewError = Object.assign(new Error("Corporate View Only"), {
      ...responses,
      isCorporateViewOnly: true,
    });
    throw corporateViewError;
  }

  let accountsMapping: Account[];
  if (isFeatureToggleRBACEnabled(ftRBACToggle)) {
    accountsMapping = await accountMappings
      .then((response) => response.data)
      .catch((e) => {
        throw e;
      });
  } else if (isDealerServiceApiAuthEnable) {
    accountsMapping = await dealerApiHook
      .accountsLoader(dealerUrl, userId)
      .then((response) => response.data)
      .catch((e) => {
        throw e;
      });
  } else {
    accountsMapping = await DealerApi.accountsLoader(
      dealerUrl,
      userId,
      token.accessToken,
      isClientUI
    )
      .then((response) => response.data)
      .catch((e) => {
        throw e;
      });
  }

  return {
    xId: userId,
    roles: responses.roles,
    businessActivities: responses.businessActivities,
    name: `${firstName} ${lastName}`,
    isAuthorizedTo: (businessAction: BusinessAction) =>
      checkIfUserAuthorizedForAction(
        businessAction,
        responses.businessActivities
      ),
    isUserWithJDRole: () => checkIfUserHasJDRole(responses.roles),
    is: (role: RBACRole) => checkIfUserAuthorizedForRole(role, responses.roles),
    hasSetDefaultAccess: hasDefaultAccess,
    accounts: accountsMapping,
  };
};

export const UserProvider = ({
  children,
  initialValue,
  isErrorOccurred: isErrorOccurredInProvidersFederation = false,
  error: errorInProvidersFederation,
}: {
  children: ReactElement;
  initialValue?: UserDetails;
  isErrorOccurred?: boolean;
  error?: any;
}) => {
  const {
    RBAC_SERVICE_URL,
    DEALER_SERVICE_URL,
    ENABLE_FT_RBAC_INTEGRATION,
    ENABLE_RBAC_AUTH_SUPPORT,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_DEALER_SERVICE,
    ENABLE_CORPORATE_VIEW_DPA_ROLE,
  } = useContext(ConfigContext);

  const { authState } = useOktaAuth();
  const [showError, setShowError] = useState<ApplicationError>(
    errorInProvidersFederation
  );
  const dealerApiHook = useDealerApi();
  const userXId = useEffectiveUserId();
  const { isDelegating } = useDelegateContext();
  const firstName = decodeHtmlEntities(
    authState?.accessToken?.claims?.firstName as string
  );
  const lastName = decodeHtmlEntities(
    authState?.accessToken?.claims?.lastName as string
  );
  const { userDetails: userDetailsFromContext, mappingAccounts } =
    useContext(RBACAccessContext);

  const isDealerServiceApiAuthEnable =
    ENABLE_RBAC_AUTH_SUPPORT && ENABLE_RBAC_AUTH_SUPPORT_FOR_DEALER_SERVICE;

  const [userDetails, setUserDetails] = useState<UserDetails | undefined>(
    initialValue?.roles === undefined || isErrorOccurredInProvidersFederation
      ? undefined
      : {
          xId: userXId,
          name: `${firstName} ${lastName}`,
          roles: initialValue.roles,
          businessActivities: initialValue.businessActivities,
          isAuthorizedTo: (businessAction: BusinessAction) =>
            checkIfUserAuthorizedForAction(
              businessAction,
              initialValue.businessActivities
            ),
          isUserWithJDRole: () => checkIfUserHasJDRole(initialValue.roles),
          is: (role: RBACRole) =>
            checkIfUserAuthorizedForRole(role, initialValue.roles),
          hasSetDefaultAccess: initialValue.hasSetDefaultAccess,
          accounts: initialValue.accounts,
        }
  );

  const setViewAccess = () => {
    setUserDetails({
      xId: userXId,
      name: `${firstName} ${lastName}`,
      roles: [RBACRole.VIEW_ACCESS],
      businessActivities: BUSINESS_ACTIONS_FOR_VIEW_ACCESS,
      isAuthorizedTo: (businessAction: BusinessAction) =>
        checkIfUserAuthorizedForAction(businessAction, []),
      isUserWithJDRole: () => checkIfUserHasJDRole([RBACRole.VIEW_ACCESS]),
      is: (role: RBACRole) =>
        checkIfUserAuthorizedForRole(role, [RBACRole.VIEW_ACCESS]),
      hasSetDefaultAccess: true,
      accounts: [],
    });
  };

  useEffect(() => {
    if (
      isErrorOccurredInProvidersFederation &&
      errorInProvidersFederation?.response?.status !== 401 &&
      errorInProvidersFederation?.roles?.length !== 0 &&
      !errorInProvidersFederation?.isCorporateViewOnly
    ) {
      setViewAccess();
    }

    if (!initialValue && !isErrorOccurredInProvidersFederation) {
      loadAccountsAndRoles(
        RBAC_SERVICE_URL,
        DEALER_SERVICE_URL,
        authState?.accessToken as AccessToken,
        userXId,
        userDetailsFromContext,
        mappingAccounts,
        ENABLE_FT_RBAC_INTEGRATION,
        isDealerServiceApiAuthEnable,
        dealerApiHook,
        ENABLE_CORPORATE_VIEW_DPA_ROLE,
        !isDelegating
      )
        .then(setUserDetails)
        .catch((e: any) => {
          setShowError(handleUserProviderErrors(e));
        });
    }
  }, [authState]);

  if (
    userDetails === undefined &&
    !showError &&
    !isErrorOccurredInProvidersFederation
  )
    return <LoadingIndicator color="#367c2b" />;

  return (
    <UserContext.Provider value={userDetails}>{children}</UserContext.Provider>
  );
};
